/**
 * ProtectedRoute Component
 * 
 * Route-level access control.
 * Protects routes based on user permissions or roles.
 * 
 * Usage:
 * - Wrap routes that require authorization
 * - Show unauthorized screen (403) if access denied
 * - Optionally redirect to login or home
 */

import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthorization } from '../auth/useAuthorization';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../auth/permissions';
import { Role } from '../auth/roles';

interface ProtectedRouteProps {
  /**
   * Child route component
   */
  children: React.ReactNode;
  
  /**
   * Required permission(s) - user must have at least one
   */
  permission?: Permission | string;
  
  /**
   * Required permissions - user must have at least one (OR logic)
   */
  anyPermission?: (Permission | string)[];
  
  /**
   * Required permissions - user must have all (AND logic)
   */
  allPermissions?: (Permission | string)[];
  
  /**
   * Required role - user must have this role
   * Note: Prefer permission checks over role checks
   */
  role?: Role | string;
  
  /**
   * Required roles - user must have at least one (OR logic)
   */
  anyRole?: (Role | string)[];
  
  /**
   * Redirect path if unauthorized (default: '/')
   */
  redirectTo?: string;
  
  /**
   * Show unauthorized screen instead of redirecting
   */
  showUnauthorized?: boolean;
  
  /**
   * Custom authorization check function
   */
  customCheck?: () => boolean;
}

/**
 * Unauthorized Screen Component
 */
const UnauthorizedScreen: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="text-6xl font-bold text-red-600 dark:text-red-400 mb-4">403</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Access Denied
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          You don't have permission to access this resource.
        </p>
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

/**
 * ProtectedRoute - Route-level access control
 * 
 * @example
 * ```tsx
 * // Permission-based
 * <Route
 *   path="/users"
 *   element={
 *     <ProtectedRoute permission={PERMISSIONS.USER_LIST}>
 *       <Users />
 *     </ProtectedRoute>
 *   }
 * />
 * 
 * // Role-based
 * <Route
 *   path="/admin"
 *   element={
 *     <ProtectedRoute role={ROLES.ADMIN} showUnauthorized>
 *       <AdminPanel />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  anyPermission,
  allPermissions,
  role,
  anyRole,
  redirectTo = '/game/oneday#home',
  showUnauthorized = false,
  customCheck,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAuthenticated,
    userRoles,
  } = useAuthorization();
  
  // Get raw authenticated state to check for loading (null)
  const { authenticated } = useAuth();

  // Track if we've already shown the toast and redirected for this unauthorized access
  const hasShownToastRef = useRef(false);
  const lastPathRef = useRef<string | null>(null);

  // Determine if user is authorized
  let authorized = false;

  if (customCheck) {
    authorized = customCheck();
  } else if (permission) {
    authorized = hasPermission(permission);
  } else if (anyPermission && anyPermission.length > 0) {
    authorized = hasAnyPermission(anyPermission);
  } else if (allPermissions && allPermissions.length > 0) {
    authorized = hasAllPermissions(allPermissions);
  } else if (role) {
    authorized = hasRole(role);
  } else if (anyRole && anyRole.length > 0) {
    authorized = hasAnyRole(anyRole);
  } else {
    // No authorization check specified - allow by default
    authorized = true;
  }

  // Check if there's an authorization requirement
  const hasAuthRequirement = !!(permission || anyPermission || allPermissions || role || anyRole || customCheck);

  // Handle authentication and authorization redirects
  useEffect(() => {
    // Wait for auth state to be determined (not null) before making redirect decisions
    if (authenticated === null) {
      // Auth state is still loading, don't redirect yet
      return;
    }

    // Reset toast flag if path changed
    const currentPath = location.pathname;
    if (lastPathRef.current !== currentPath) {
      hasShownToastRef.current = false;
      lastPathRef.current = currentPath;
    }

    // Recalculate authorization inside useEffect to ensure we use the latest roles
    let currentAuthorized = false;
    if (customCheck) {
      currentAuthorized = customCheck();
    } else if (permission) {
      currentAuthorized = hasPermission(permission);
    } else if (anyPermission && anyPermission.length > 0) {
      currentAuthorized = hasAnyPermission(anyPermission);
    } else if (allPermissions && allPermissions.length > 0) {
      currentAuthorized = hasAllPermissions(allPermissions);
    } else if (role) {
      currentAuthorized = hasRole(role);
    } else if (anyRole && anyRole.length > 0) {
      currentAuthorized = hasAnyRole(anyRole);
    } else {
      // No authorization check specified - allow by default
      currentAuthorized = true;
    }

    // Reset toast flag if path changed (already handled above)

    // If user doesn't have authorization (permission/role), always redirect to game list
    // Never redirect to login for authorization failures
    // Only redirect if we're sure auth and roles are loaded
    if (!currentAuthorized && hasAuthRequirement && authenticated === true) {
      // Only show toast once per unauthorized access attempt
      if (!hasShownToastRef.current) {
        // Show error toast
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: {
            message: 'You do not have permission to access this page.',
            type: 'error'
          }
        }));
        hasShownToastRef.current = true;
      }
      
      // Always redirect to game list for unauthorized access
      const redirectPath = redirectTo || '/game/oneday#home';
      navigate(redirectPath, { replace: true });
      return;
    }

    // Only redirect to login if user is not authenticated AND there's no authorization requirement
    // (i.e., it's just a protected route, not a permission-based route)
    if (!isAuthenticated && !hasAuthRequirement) {
      navigate('/login', { replace: true, state: { from: location } });
      return;
    }

    // If not authenticated but there IS an authorization requirement, 
    // treat it as unauthorized and redirect to game list (not login)
    if (!isAuthenticated && hasAuthRequirement) {
      // Only show toast once per unauthorized access attempt
      if (!hasShownToastRef.current) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: {
            message: 'You do not have permission to access this page.',
            type: 'error'
          }
        }));
        hasShownToastRef.current = true;
      }
      
      const redirectPath = redirectTo || '/game/oneday#home';
      navigate(redirectPath, { replace: true });
    }
  }, [authenticated, isAuthenticated, authorized, hasAuthRequirement, userRoles, navigate, redirectTo, location]);

  // Wait for auth state to be determined (not null) before making decisions
  if (authenticated === null) {
    // Auth state is still loading, show loading state or return null
    return null;
  }

  // If not authorized (and has auth requirement), return null (will redirect via useEffect)
  if (!authorized && hasAuthRequirement) {
    return null;
  }

  // If not authenticated and no auth requirement, return null (will redirect to login via useEffect)
  if (!isAuthenticated && !hasAuthRequirement) {
    return null;
  }

  // If not authenticated but has auth requirement, treat as unauthorized
  if (!isAuthenticated && hasAuthRequirement) {
    return null;
  }

  // Authorized - render children
  return <>{children}</>;
};

export default ProtectedRoute;

// Ensure this file is treated as a module
export {};

